import { Box, Card, IconButton, Typography } from '@mui/material';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Favorite, FavoriteBorder } from '@mui/icons-material';
import Image from '../image/Image';
import IRecipe from '../../interfaces/IRecipe';
import { favoritedColor, secondary, straightFont } from '../../Constants';
import useRouter from '../../hooks/use-router';
import { useModal } from '../../providers/ModalProvider';
import { updateUserFavorites } from '../../services/DbService';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../providers/ThemeProvider';
import { getRecipeUrl } from '../../utils/seo';

interface RecipeMiniCardProps {
  recipe: IRecipe;
  setShowSearch: Dispatch<SetStateAction<boolean>>;
}

export default function RecipeMiniCard(props: RecipeMiniCardProps) {
  const [isFavorited, setIsFavorited] = useState(false);
  const { recipe, setShowSearch } = props;
  const { mode, colors } = useTheme();

  const { currentUser, setCurrentUser } = useAuth();
  const { setShowLoginModal, setTriggerFavMessage } = useModal();

  const linkTo = getRecipeUrl(recipe);
  const router = useRouter();

  const handleClick = () => {
    setShowSearch(false);
    router.push(linkTo);
  };

  const handleFavoriteClick = async () => {
    if (currentUser) {
      await updateUserFavorites(currentUser, recipe.id);

      const updatedFavorites = currentUser.favorites.includes(recipe.id)
        ? currentUser.favorites.filter((id) => id !== recipe.id)
        : [...currentUser.favorites, recipe.id];

      setCurrentUser({
        ...currentUser,
        favorites: updatedFavorites,
      });

      setIsFavorited((prev) => !prev);
    } else {
      setTriggerFavMessage(true);
      setShowLoginModal(true);
    }
  };

  useEffect(() => {
    if (currentUser) {
      setIsFavorited(currentUser.favorites.includes(recipe.id));
    }
  }, [currentUser, recipe.id, currentUser?.favorites]);

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: 'pointer',
        '&:hover': {
          '.image': {
            transform: 'scale(1.05)',
          },
          '.title': {
            color: secondary,
          },
          '.card': {
            boxShadow: mode === 'light' ? '0 6px 12px rgba(0, 0, 0, 0.15)' : '0 6px 12px rgba(0, 0, 0, 0.4)',
          },
        },
      }}
    >
      {/* Card Container */}
      <Card
        className="card"
        sx={{
          height: 165,
          width: 165,
          borderRadius: 3,
          position: 'relative',
          boxShadow: mode === 'light' ? '0px 2px 6px rgba(0, 0, 0, 0.1)' : '0px 2px 6px rgba(0, 0, 0, 0.3)',
          border: '1px solid',
          borderColor: colors.border,
          transition: 'box-shadow 0.3s ease, transform 0.3s ease',
          overflow: 'hidden',
          backgroundColor: colors.cardBackground,
          '&:hover': {
            transform: 'scale(1.02)',
          },
        }}
      >
        {/* Favorite Button */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
          }}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleFavoriteClick();
            }}
            sx={{
              color: isFavorited ? favoritedColor : colors.textSecondary,
              backgroundColor: 'rgba(255,255,255,0.9)',
              borderRadius: '50%',
              padding: '4px',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,1)',
                transform: 'scale(1.1)',
              },
            }}
          >
            {isFavorited ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
          </IconButton>
        </Box>

        {/* Image */}
        <Box className="image" sx={{ transition: 'transform 0.3s ease' }}>
          <Image
            alt={recipe.title}
            src={recipe.image}
            ratio="1/1"
            sx={{
              borderRadius: 3,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </Box>
      </Card>

      {/* Title */}
      <Typography
        className="title"
        sx={{
          fontFamily: straightFont,
          fontSize: '0.875rem',
          fontWeight: 500,
          color: colors.text,
          textAlign: 'center',
          maxWidth: '95%',
          mt: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          transition: 'color 0.2s ease',
        }}
      >
        {recipe.title}
      </Typography>
    </Box>
  );
}
